/**
 * 🔊 Combat Sound Engine (Feb 2026)
 *
 * Lightweight Web Audio + HTMLAudioElement hybrid for battle SFX.
 * Designed for:
 *   • Solo Wilds animated replay (one-shot heavy/light/crit + victory/defeat)
 *   • Party Combat polling overlay (same SFX vocabulary)
 *
 * Why this design?
 *   • CC0 mp3/wav assets hosted on mixkit/freesound CDNs — no API keys, no
 *     bundle bloat, no licensing risk.
 *   • Lazily preloaded on first interaction (iOS Safari autoplay-policy
 *     friendly — the user clicking "Attack" counts as the gesture).
 *   • Mute persisted to localStorage so it carries across sessions.
 *   • All exposed via a single `useCombatSounds()` hook + an exported
 *     <CombatSoundToggle/> button you can drop into any header.
 *
 * SFX vocabulary (minimal ship — owner-locked 2026-02):
 *   sfx.hit_heavy()   — heavy chunky impact
 *   sfx.hit_light()   — quick slash whoosh
 *   sfx.crit()        — sparkly bell ping (layered on top of heavy/light)
 *   sfx.victory()     — short triumphant stinger
 *   sfx.defeat()      — short somber stinger
 *   sfx.skill()       — generic magical zap (placeholder for future class cues)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// All free / CC0 licensed via mixkit.co (no key, no attribution required for use)
const SOUND_URLS = {
  hit_heavy: "https://assets.mixkit.co/active_storage/sfx/2435/2435-preview.mp3",
  hit_light: "https://assets.mixkit.co/active_storage/sfx/2434/2434-preview.mp3",
  crit:      "https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3",
  victory:   "https://assets.mixkit.co/active_storage/sfx/270/270-preview.mp3",
  defeat:    "https://assets.mixkit.co/active_storage/sfx/553/553-preview.mp3",
  skill:     "https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3",
};

const STORAGE_KEY = "omni:combat-mute";

// Global cache of preloaded HTMLAudioElement clones so simultaneous turns
// don't cut each other off mid-play. We rotate through a small pool.
const POOL_SIZE = 3;
const audioPool = {};
function getPooledAudio(key) {
  if (!audioPool[key]) {
    const url = SOUND_URLS[key];
    if (!url) return null;
    audioPool[key] = Array.from({ length: POOL_SIZE }, () => {
      const a = new Audio(url);
      a.preload = "auto";
      return a;
    });
    audioPool[key].idx = 0;
  }
  const pool = audioPool[key];
  const a = pool[pool.idx % POOL_SIZE];
  pool.idx = (pool.idx + 1) % POOL_SIZE;
  return a;
}

function readMutePersisted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMutePersisted(val) {
  try {
    localStorage.setItem(STORAGE_KEY, val ? "1" : "0");
  } catch {}
}

/**
 * useCombatSounds — returns { play, mute, setMute, isMuted }
 *   • play("hit_heavy" | "hit_light" | "crit" | "victory" | "defeat" | "skill")
 *   • mute / setMute / isMuted toggle persistence
 */
export function useCombatSounds() {
  const [muted, setMuted] = useState(() => readMutePersisted());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const setMutePersisted = useCallback((val) => {
    setMuted(val);
    writeMutePersisted(val);
  }, []);

  const toggleMute = useCallback(() => {
    setMutePersisted(!mutedRef.current);
  }, [setMutePersisted]);

  const play = useCallback((key, opts = {}) => {
    if (mutedRef.current) return;
    const a = getPooledAudio(key);
    if (!a) return;
    try {
      a.currentTime = 0;
      a.volume = Math.max(0, Math.min(1, opts.volume ?? 0.45));
      // play() returns a promise that rejects on autoplay-policy block — swallow it
      a.play()?.catch(() => {});
    } catch {}
  }, []);

  return useMemo(
    () => ({ play, isMuted: muted, toggleMute, setMute: setMutePersisted }),
    [play, muted, toggleMute, setMutePersisted]
  );
}

/**
 * <CombatSoundToggle/> — small icon button. Drop into any header.
 * Props: { className?, size? }
 */
export function CombatSoundToggle({ className = "", size = "sm" }) {
  const { isMuted, toggleMute } = useCombatSounds();
  // Force a re-render of the toggle when other components flip the mute state.
  // (Simple cross-component sync via a window-level event.)
  const [, force] = useState(0);
  useEffect(() => {
    const onSync = () => force((n) => n + 1);
    window.addEventListener("omni:combat-mute-changed", onSync);
    return () => window.removeEventListener("omni:combat-mute-changed", onSync);
  }, []);

  const onClick = () => {
    toggleMute();
    window.dispatchEvent(new Event("omni:combat-mute-changed"));
  };

  const pad = size === "sm" ? "px-2 py-1 text-[10px]" : "px-3 py-2 text-xs";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="combat-sound-toggle"
      className={`rounded-md border border-amber-700/40 bg-black/40 hover:bg-amber-900/30 text-amber-200 font-bold tracking-wider uppercase ${pad} ${className}`}
      title={isMuted ? "Combat sound is OFF — click to enable" : "Combat sound is ON — click to mute"}
    >
      {isMuted ? "🔇 SOUND OFF" : "🔊 SOUND ON"}
    </button>
  );
}
